import { useEffect, useRef, useState } from "react";
import Icon from "./Icon";

type Props = {
  changeSelectedTabs: (selectedTabs: string[]) => void;
  tabs: string[];
  /**
   * If defined, the value will be used to access and store the selected tabs within the Browser's
   * SessionStorage. If this is undefined, the tabs will not be stored.
   */
  sessionStorageKey?: string;
};

// TODO: find a good way to mock SessionStorage.
/**
 * Component provides functionality to do the following
 * - gets the tabs to display, includes loading from session stroage
 * - allow updating tabs, includes setting the session storage
 * - toggle tab, but prevent hiding all fields at once
 * - render tab display list
 */
const TabList = (props: Props) => {
  // displayTabs contains a distinct array of tabs, with the '' property sorted to the end of the
  // array. cannot be set after initial load.
  const [displayTabs] = useState<string[]>(() => {
    const displayTabs: string[] = [];
    // create set of display tabs
    const displayTabSet = new Set(props.tabs);
    displayTabSet.forEach((x) => displayTabs.push(x));
    // shift '' to end if it exists
    if (displayTabSet.has("")) {
      displayTabs.push(...displayTabs.splice(displayTabs.indexOf(""), 1));
    }
    return displayTabs;
  });
  const [selectedTabs, setSelectedTabs] = useState<string[]>(() => {
    // determine initial selected tabs using SessionStorage, if applicable.
    if (!props.sessionStorageKey) {
      return Array.from(displayTabs);
    }

    const data_serialized = sessionStorage.getItem(props.sessionStorageKey);
    let storedSelectedTabs: string[] = [];
    // process data, make sure its not broken
    try {
      storedSelectedTabs = JSON.parse(data_serialized ?? "");
      // unsure what type we just deserialized is, just yolo it
      storedSelectedTabs = (storedSelectedTabs as string[])
        // filter out anything that isn't in our list
        .filter((x) => displayTabs.indexOf(x) != -1);
    } catch (e) {
      storedSelectedTabs = [];
    }

    // don't let nothing be shown
    if (storedSelectedTabs.length == 0) {
      storedSelectedTabs.push(...displayTabs);
    }

    return storedSelectedTabs;
  });

  useEffect(() => {
    // update storage when selected tabs changes, tell parent tabs changed
    if (props.sessionStorageKey) {
      sessionStorage.setItem(
        props.sessionStorageKey,
        JSON.stringify(selectedTabs),
      );
    }
    props.changeSelectedTabs(selectedTabs);
  }, [selectedTabs]);

  const toggleTab = (targetTab: string, visible: boolean) => {
    if (visible) {
      if (selectedTabs.length === 1) {
        return; // don't allow hiding of all fields
      }
      setSelectedTabs([...selectedTabs.filter((x) => x !== targetTab)]);
    } else {
      setSelectedTabs([...selectedTabs, targetTab]);
    }
  };

  if (displayTabs.length === 1) {
    return <></>;
  }
  return (
    /** TODO: this should be it's own component probably */
    <div className={"displayTabList"}>
      {displayTabs.map((tab) => {
        const isUncategorized = tab === "";
        const isEnabled = selectedTabs.indexOf(tab) > -1;
        return (
          <button
            key={tab}
            onClick={() => {
              toggleTab(tab, isEnabled);
            }}
            className={`displayTabBox ${isUncategorized ? "uncategorized" : ""} ${isEnabled ? "enabled" : "disabled"}`}
          >
            <span>
              {!isUncategorized ? tab : "Uncategorized"}
              <Icon
                altText={`Toggle ${tab} tab visibility`}
                icon={isEnabled ? "eye" : "eye-slash"}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
};

export { TabList };
